import {
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery,
} from "@apollo/client";
import {
  OfficeBuildingIcon,
  PencilIcon,
  PlusCircleIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/outline";
import { sortBy, uniqBy } from "lodash";
import { Fragment, ReactNode, useMemo, useState } from "react";
import {
  CareerTrackRoleType,
  CompetencyCriteriaUniqueness,
  DeleteOrgWideCareerLevelMutation,
  DeleteOrgWideCareerLevelMutationVariables,
  DeleteOrgWideCareerTrackMutation,
  DeleteOrgWideCareerTrackMutationVariables,
  GetAllOrganizationTeamsQueryQuery,
  GetAllOrganizationTeamsQueryQueryVariables,
  GetOrganizationCareerTracksQuery,
  GetOrganizationCareerTracksQueryVariables,
  GetOrganizationCompetenciesQuery,
  GetOrganizationCompetenciesQueryVariables,
  GetOrganizationTeamQueryQuery,
  GetOrganizationTeamQueryQueryVariables,
  GetUnassignedMemberCountQuery,
  GetUnassignedMemberCountQueryVariables,
  OrganizationCompetencyFragment,
  SaveOrgWideCareerTrackMutation,
  SaveOrgWideCareerTrackMutationVariables,
  SaveOrgWideCompetencyMutation,
  SaveOrgWideCompetencyMutationVariables,
} from "types/graphql-schema";

import getAllOrganizationTeamsQuery from "@apps/artifact/graphql/get-all-organization-teams-query";
import TeamEditDialog from "@apps/org-settings/components/team-edit-dialog";
import createOrUpdateCareerTrackMutation from "@apps/org-settings/graphql/create-or-update-career-track-mutation";
import createOrUpdateOrgWideCompetencyMutation from "@apps/org-settings/graphql/create-or-update-org-wide-competency-mutation";
import deleteCareerLevelMutation from "@apps/org-settings/graphql/delete-career-level-mutation";
import deleteOrgWideCareerTrackMutation from "@apps/org-settings/graphql/delete-org-wide-career-track-mutation";
import getOrganizationCareerTracksQuery from "@apps/org-settings/graphql/get-organization-career-tracks-query";
import getOrganizationCompetenciesQuery from "@apps/org-settings/graphql/get-organization-competencies-query";
import getOrganizationTeamQuery from "@apps/org-settings/graphql/get-organization-team-query";
import getUnassignedMemberCountQuery from "@apps/org-settings/graphql/get-unassigned-member-count-query";
import useLabel from "@apps/use-label/use-label";
import { successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import AppLink from "@components/link/link";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import {
  UserComboboxOptionType,
  UserComboboxTeamOption,
} from "@components/user-combobox/user-combobox-list";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull, assertNonNull } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import SettingsPageHeader from "../settings-page-header";
import AddButton from "./add-button";
import CareerTrackDetailsModal from "./career-track-form/career-track-details-modal";
import { CareerTrackFormRole } from "./career-track-form/career-track-form";
import RoleDetailModal from "./career-track-form/role-detail-modal";
import CareerTrackViewSwitcher from "./career-track-view-switcher";
import CompetenciesList from "./competencies-list";
import CompetencyFormModal from "./competency-form-modal";
import {
  sectionHeaderHoverToneClassName,
  sectionHeaderToneClassName,
} from "./competency-sharing-tone";
import {
  careerTrackPath,
  departmentCompetenciesPath,
  describeRoleHolders,
  getCompetencyScopeSection,
  getDefaultRoleTitle,
  getRolesAndCompetenciesBreadcrumbs,
  isDepartmentCompetency,
  membersWithoutRolePath,
  removedIds,
  roleLevelIterator,
} from "./helpers";
import { summarizeLevelDeletion } from "./level-delete-count";
import LevelDeletionMessage from "./level-delete-message";
import NoTracksNotice from "./no-tracks-notice";
import StickyColumnShadow from "./sticky-column-shadow";

// Narrower than the editor's columns: these cells hold a role name or a
// count, not criteria.
const levelColWidth = "w-64 max-w-64 min-w-64";

// Wider than the editor's first column: this one carries a department name
// and the buttons for adding to it.
const nameColWidth = "w-96 min-w-96 max-w-96";

// Row actions stay out of the way until the row (or cell) is pointed at.
const rowActionClassName =
  "opacity-0 transition-opacity group-hover/row:opacity-100 group-hover/cell:opacity-100 focus-within:opacity-100";

// A track with no department of its own. Sorts after the named ones.
const NO_DEPARTMENT = "none";

type OverviewTrack = NonNullable<
  NonNullable<
    GetOrganizationCareerTracksQuery["careerTracks"]["edges"][number]
  >["node"]
>;

// How many competencies write criteria at each level. Criteria written per
// role belong to a role rather than a level, so only shared ones are counted.
const criteriaCountByLevel = (
  competencies: ReadonlyArray<OrganizationCompetencyFragment>,
) =>
  competencies
    .filter(
      (competency) =>
        competency.criteriaUniqueness ===
        CompetencyCriteriaUniqueness.SameAcrossRoles,
    )
    .reduce<Record<number, number>>((counts, competency) => {
      const levels = new Set(
        assertEdgesNonNull(competency.criteria).map(({ level }) => level),
      );
      levels.forEach((level) => {
        counts[level] = (counts[level] ?? 0) + 1;
      });
      return counts;
    }, {});

const SectionHeaderRow = ({
  title,
  before,
  after,
  indentClassName,
  toneClassName,
  hoverClassName,
  levelCount,
  countByLevel,
  countLabel,
  emptyLabel,
  onOpen,
  onDeleteLevel,
  action,
}: {
  title: string;
  // Beside the name: a department's chevron, and what it hides when folded.
  before?: ReactNode;
  after?: ReactNode;
  // How deep in the hierarchy the row sits: departments, then the tracks
  // inside them.
  indentClassName: string;
  toneClassName: string;
  hoverClassName?: string;
  levelCount: number;
  countByLevel: Record<number, number>;
  countLabel: (count: number) => string;
  // What a level with nothing written on it says. Left unset for a grouping
  // that isn't a scope of its own, which has no count to be empty.
  emptyLabel?: string;
  onOpen?: () => void;
  // Removes a level. A level is organization-wide, so this is the same act
  // whichever of these rows it is started from.
  onDeleteLevel?: (level: number) => void;
  action?: ReactNode;
}) => (
  <tr
    className={classNames(
      toneClassName,
      "group/row",
      onOpen && "cursor-pointer",
    )}
    role={onOpen ? "link" : undefined}
    tabIndex={onOpen ? 0 : undefined}
    aria-label={onOpen ? `Overview: Open ${title}` : undefined}
    onClick={onOpen}
    onKeyDown={(evt) => {
      if (evt.key === "Enter") {
        onOpen?.();
      }
    }}
  >
    <td
      className={classNames(
        nameColWidth,
        toneClassName,
        onOpen && hoverClassName,
        indentClassName,
        "pr-4 py-2 font-bold border border-l-0 sticky left-0 z-10",
      )}
    >
      <div className="flex items-center justify-between gap-2 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          {before}
          <div className="truncate" title={title}>
            {title}
          </div>
          {after}
        </div>
        <div className="shrink-0 flex items-center">{action}</div>
      </div>
      <StickyColumnShadow />
    </td>
    {roleLevelIterator(levelCount).map((level) => {
      const count = countByLevel[level] ?? 0;
      return (
        <td
          key={level}
          className={classNames(
            levelColWidth,
            toneClassName,
            onOpen && hoverClassName,
            "px-4 py-2 border font-normal",
          )}
        >
          <div className="flex items-center justify-between gap-2">
            <span
              className={classNames(
                "truncate",
                count === 0 && "font-normal text-gray-400",
              )}
            >
              {count > 0 ? countLabel(count) : (emptyLabel ?? "")}
            </span>
            {/* Only where something is actually written: an empty cell has
                nothing to delete, and the act reaches every track. */}
            {onDeleteLevel && count > 0 && (
              <Button
                theme={buttonTheme.iconGray}
                icon={TrashIcon}
                size={ButtonSize.medium}
                className={classNames(rowActionClassName, "-my-1 shrink-0")}
                tooltip="Delete these criteria"
                aria-label={`Overview: Delete level ${level} from ${title}`}
                onClick={(evt) => {
                  evt.stopPropagation();
                  onDeleteLevel(level);
                }}
              />
            )}
          </div>
        </td>
      );
    })}
    <td
      className={classNames(
        toneClassName,
        onOpen && hoverClassName,
        "min-w-16 px-4 py-2 border border-r-0",
      )}
    />
  </tr>
);

// A row's "+": what can be made from here, in a menu. The row behind it
// opens a page, so clicks in the menu (a portal, but still the row's child in
// React) stop here.
const RowAddMenu = ({
  ariaLabel,
  options,
}: {
  ariaLabel: string;
  options: DropdownOptionType[];
}) => (
  <div onClick={(evt) => evt.stopPropagation()}>
    <Dropdown align="end" options={options}>
      <Button
        theme={buttonTheme.iconGray}
        icon={TopicflowIconMap.plus}
        size={ButtonSize.medium}
        tooltip="Add"
        aria-label={ariaLabel}
      />
    </Dropdown>
  </div>
);

// Every department and career track in the organization, in one table: the
// way in to each of the editor's views. Section headers count the
// competencies written at that level; a track's cells name the role that sits
// there.
const CareerTrackOverview = ({
  organizationId,
}: {
  organizationId: number;
}) => {
  const label = useLabel();
  const link = useLink();
  const client = useApolloClient();
  const [isCreatingDepartment, setIsCreatingDepartment] = useState(false);
  // Departments start open, so every track shows; these are the ones folded.
  // Kept as the folded ones so a department that loads later starts open too.
  const [collapsedSections, setCollapsedSections] = useState<string[]>([]);
  const isSectionExpanded = (key: string) => !collapsedSections.includes(key);
  const expandSection = (key: string) =>
    setCollapsedSections((prev) => prev.filter((other) => other !== key));
  // A competency being made from a row, starting with that row's reach: the
  // whole company, a department, or one track.
  const [creatingCompetency, setCreatingCompetency] = useState<{
    startingTeams?: { id: number; title: string }[];
    startingCareerTracks?: { id: number; title: string }[];
  } | null>(null);
  // The department being edited, with its members: the dialog saves its
  // member list in full, so it opens only once the saved one is in hand.
  const [editingDepartment, setEditingDepartment] = useState<
    GetOrganizationTeamQueryQuery["team"] | null
  >(null);
  const [fetchDepartment] = useLazyQuery<
    GetOrganizationTeamQueryQuery,
    GetOrganizationTeamQueryQueryVariables
  >(getOrganizationTeamQuery, { fetchPolicy: "network-only" });
  const handleEditDepartment = async (teamId: number) => {
    const { data } = await fetchDepartment({ variables: { teamId } });
    if (data?.team) {
      setEditingDepartment(data.team);
    }
  };
  // A track's details, being named. Without a track it's a new one, started
  // in whichever department it was opened from.
  const [trackDetails, setTrackDetails] = useState<{
    track?: OverviewTrack;
    team: UserComboboxTeamOption | null;
  } | null>(null);
  // The role being edited, with the track it belongs to: saving sends the
  // whole track, so both are needed.
  const [editingRole, setEditingRole] = useState<{
    track: OverviewTrack;
    role: CareerTrackFormRole;
  } | null>(null);
  // Levels the table should show even before anything is written on them. A
  // level isn't stored in its own right, so widening here is only a view.
  // There's always at least one level, so even an empty table shows Level 1
  // and reads as a ladder.
  const [minLevelCount, setMinLevelCount] = useState(1);

  const {
    data: tracksData,
    loading: tracksLoading,
    refetch: refetchTracks,
  } = useQuery<
    GetOrganizationCareerTracksQuery,
    GetOrganizationCareerTracksQueryVariables
  >(getOrganizationCareerTracksQuery, {
    fetchPolicy: "cache-and-network",
    // Every track, unpaged: grouping by department only reads right with all
    // of them in hand, or a department looks empty while its tracks sit on
    // the next page.
    variables: { organizationId, withHolders: true },
  });

  const {
    data: teamsData,
    loading: teamsLoading,
    refetch: refetchTeams,
  } = useQuery<
    GetAllOrganizationTeamsQueryQuery,
    GetAllOrganizationTeamsQueryQueryVariables
  >(getAllOrganizationTeamsQuery, { variables: { organizationId } });

  const { data: competenciesData, refetch: refetchCompetencies } = useQuery<
    GetOrganizationCompetenciesQuery,
    GetOrganizationCompetenciesQueryVariables
  >(getOrganizationCompetenciesQuery, {
    // All Roles edits skip the cache, so the counts here come from the server.
    fetchPolicy: "cache-and-network",
    variables: { organizationId },
  });

  const [saveCompetency] = useMutation<
    SaveOrgWideCompetencyMutation,
    SaveOrgWideCompetencyMutationVariables
  >(createOrUpdateOrgWideCompetencyMutation);

  const [saveCareerTrack] = useMutation<
    SaveOrgWideCareerTrackMutation,
    SaveOrgWideCareerTrackMutationVariables
  >(createOrUpdateCareerTrackMutation, {
    onCompleted: () => {
      refetchTracks();
      refetchUnassigned();
    },
  });

  // A save sends the track's title and department (none clears it), so they
  // come from what's saved now: this page's copy can be out of date. Null if
  // it's gone.
  const fetchSavedTrack = async (trackId: number) => {
    const fresh = await refetchTracks();
    return (
      assertEdgesNonNull(fresh.data.careerTracks).find(
        ({ id }) => id === trackId,
      ) ?? null
    );
  };

  // Editing a role saves the track it sits on, with that role alone: the
  // others, and what this one leaves out (criteria, responsibilities), stay.
  const handleRoleChange = async (
    { id: trackId }: OverviewTrack,
    // The role as the dialog opened on it, and as it was saved.
    starting: CareerTrackFormRole,
    updated: CareerTrackFormRole,
  ) => {
    const track = await fetchSavedTrack(trackId);
    if (!track) {
      return;
    }
    const savedRole = assertEdgesNonNull(track.roles).find(
      (role) => role.id === updated.id,
    );
    // Deleted meanwhile: there's nothing left to edit.
    if (updated.id && !savedRole) {
      return;
    }
    // Anyone taken off in the dialog who still holds it: their assignment
    // ends.
    const savedHolderIds = savedRole
      ? assertEdgesNonNull(savedRole.assignedUsers).map(({ id }) => id)
      : [];
    const unassignUserIds = removedIds(
      starting.assignToUsers,
      updated.assignToUsers,
    ).filter((id) => savedHolderIds.includes(id));
    await saveCareerTrack({
      variables: {
        organizationId,
        careerTrackId: track.id,
        title: track.title,
        teamId: track.team?.id ?? null,
        roles: [
          savedRole
            ? {
                id: savedRole.id,
                // Only what the dialog changed: the rest may have been
                // changed elsewhere since it opened.
                ...(updated.title !== starting.title
                  ? { title: updated.title }
                  : {}),
                ...(updated.roleType !== starting.roleType
                  ? { roleType: updated.roleType }
                  : {}),
                // Only who was added: someone ended elsewhere since isn't
                // put back.
                assignToUsers: removedIds(
                  updated.assignToUsers,
                  starting.assignToUsers,
                ),
                unassignUserIds,
              }
            : // A role with no id yet is one the "+" started.
              {
                level: updated.level,
                title:
                  updated.title?.trim() ||
                  getDefaultRoleTitle(track.title, updated.level ?? 1),
                roleType: updated.roleType,
                assignToUsers: updated.assignToUsers.map(({ id }) => id),
              },
        ],
      },
    });
  };

  const {
    confirm: askDeleteTrack,
    setMessage: setDeleteTrackMessage,
    ConfirmationDialog: ConfirmDeleteTrackDialog,
  } = useConfirm("Delete career track?", "");
  const {
    confirm: askDeleteLevel,
    setMessage: setDeleteLevelMessage,
    setTitle: setDeleteLevelTitle,
    ConfirmationDialog: ConfirmDeleteLevelDialog,
  } = useConfirm("Clear level?", "");
  const {
    confirm: askDeleteLevelEverywhere,
    setMessage: setDeleteLevelEverywhereMessage,
    setTitle: setDeleteLevelEverywhereTitle,
    ConfirmationDialog: ConfirmDeleteLevelEverywhereDialog,
  } = useConfirm("Delete level?", "");

  const [deleteCareerLevel] = useMutation<
    DeleteOrgWideCareerLevelMutation,
    DeleteOrgWideCareerLevelMutationVariables
  >(deleteCareerLevelMutation);

  const [deleteCareerTrack] = useMutation<
    DeleteOrgWideCareerTrackMutation,
    DeleteOrgWideCareerTrackMutationVariables
  >(deleteOrgWideCareerTrackMutation);

  // The overview is where you were, so it is where creating leaves you: the
  // new track appears in its department rather than opening its own editor.
  const handleCreateTrack = async (
    title: string,
    team: UserComboboxTeamOption | null,
  ) => {
    await saveCareerTrack({
      variables: {
        organizationId,
        title,
        teamId: team?.id ?? null,
        // A track with no rung on it has nothing to show, so it starts with
        // one, the way the editor's new tracks do.
        roles: [
          {
            level: 1,
            title: getDefaultRoleTitle(title, 1),
            roleType: CareerTrackRoleType.Ic,
          },
        ],
      },
    });
    expandSection(team ? `team-${team.id}` : NO_DEPARTMENT);
    successNotificationVar({ title: `${title} created` });
  };

  // Renaming or re-filing a track. Its roles aren't sent, so they stay.
  const handleTrackDetailsChange = async (
    // The track as the dialog opened on it.
    opened: OverviewTrack,
    title: string,
    team: UserComboboxTeamOption | null,
  ) => {
    const track = await fetchSavedTrack(opened.id);
    if (!track) {
      return;
    }
    // Only what the dialog changed: the rest may have changed elsewhere since
    // it opened. Left out, the name stays; the department has to be sent
    // (none clears it), so an unchanged one goes as it's saved now.
    await saveCareerTrack({
      variables: {
        organizationId,
        careerTrackId: track.id,
        title: title !== opened.title ? title : undefined,
        teamId:
          (team?.id ?? null) !== (opened.team?.id ?? null)
            ? (team?.id ?? null)
            : (track.team?.id ?? null),
      },
    });
  };

  // A level on one track is its next role: named in the role dialog.
  const startAddingLevel = (track: OverviewTrack) => {
    const nextLevel =
      Math.max(
        0,
        ...assertEdgesNonNull(track.roles).map((role) => role.level),
      ) + 1;
    setEditingRole({
      track,
      role: {
        uuid: `new-${track.id}-${nextLevel}`,
        level: nextLevel,
        title: "",
        roleType: CareerTrackRoleType.Ic,
        assignToUsers: [],
        criteriaByCompetencyId: {},
        responsibilities: [],
      },
    });
  };

  const confirmTrackDelete = async (track: OverviewTrack) => {
    const roles = assertEdgesNonNull(track.roles);
    setDeleteTrackMessage(
      `Delete "${track.title}" and its ${roles.length} role${
        roles.length === 1 ? "" : "s"
      }, with their criteria and responsibilities? ${describeRoleHolders(
        roles.flatMap((role) => assertEdgesNonNull(role.assignedUsers)),
        roles.length,
      )} ${label("review", { capitalize: true })} questions and answers about these roles are deleted too.`,
    );
    if (!(await askDeleteTrack())) {
      return;
    }
    await deleteCareerTrack({ variables: { careerTrackId: track.id } });
    // The track also comes off every competency scoped to it, and the
    // competencies table below keeps its own copy: both refresh.
    await Promise.all([
      refetchTracks(),
      refetchUnassigned(),
      client.refetchQueries({ include: [getOrganizationCompetenciesQuery] }),
    ]);
    successNotificationVar({ title: `${track.title} deleted` });
  };

  // Clears what this row has written at a level, and nothing else: the level
  // itself stays as long as anything else still sits on it.
  const confirmLevelDelete = async (
    level: number,
    teamId: number,
    scopeTitle: string,
    scopedCompetencies: ReadonlyArray<OrganizationCompetencyFragment>,
  ) => {
    const affected = scopedCompetencies.filter(
      (competency) =>
        competency.criteriaUniqueness ===
          CompetencyCriteriaUniqueness.SameAcrossRoles &&
        assertEdgesNonNull(competency.criteria).some(
          (criteria) => criteria.level === level,
        ),
    );
    if (affected.length === 0) {
      return;
    }
    // Criteria belong to the competency, not the row: one that also reaches
    // another department, or a track outside this one, loses them there too.
    const shared = affected.filter(
      (competency) =>
        assertEdgesNonNull(competency.appliesToTeams).some(
          ({ id }) => id !== teamId,
        ) ||
        assertEdgesNonNull(competency.appliesToCareerTracks).some(
          ({ id }) =>
            tracks.find((track) => track.id === id)?.team?.id !== teamId,
        ),
    );
    let sharedSubject = `${shared.length} of them ${
      shared.length === 1 ? "is" : "are"
    }`;
    if (shared.length === affected.length) {
      sharedSubject = affected.length === 1 ? "It is" : "All of them are";
    }
    const sharedSentence =
      shared.length === 0
        ? "Career track roles and every other row are left alone."
        : `${sharedSubject} also used by other ${label("team", {
            pluralize: true,
          })} or career tracks, which lose these level ${level} criteria too. Career track roles are left alone.`;
    // Only criteria go: the level, and every role on it, stays.
    setDeleteLevelTitle(`Clear ${scopeTitle} criteria at level ${level}?`);
    setDeleteLevelMessage(
      `This deletes the level ${level} criteria from ${affected.length} ${label(
        "competency",
        { pluralize: affected.length !== 1 },
      )}, and the ${label("review")} questions and answers about them. ${sharedSentence}`,
    );
    if (!(await askDeleteLevel())) {
      return;
    }
    // The ids come from what's saved now: this page's copy can predate
    // criteria added since on another page.
    const fresh = await refetchCompetencies();
    const freshCompetencies = assertEdgesNonNull(fresh.data.competencies);
    await Promise.all(
      affected.map((competency) => {
        const current = freshCompetencies.find(
          ({ id }) => id === competency.id,
        );
        // Switched to per-role meanwhile: its criteria are the roles' now,
        // which this doesn't touch.
        if (
          current?.criteriaUniqueness !==
          CompetencyCriteriaUniqueness.SameAcrossRoles
        ) {
          return null;
        }
        const deleteCriteriaIds = assertEdgesNonNull(current.criteria)
          .filter((criteria) => criteria.level === level)
          .map(({ id }) => id);
        return deleteCriteriaIds.length === 0
          ? null
          : saveCompetency({
              variables: { competencyId: competency.id, deleteCriteriaIds },
            });
      }),
    );
    await refetchCompetencies();
    successNotificationVar({
      title: `Level ${level} cleared for ${scopeTitle}`,
    });
  };

  const handleRoleDelete = async (
    { id: trackId }: OverviewTrack,
    roleId: number,
  ) => {
    const track = await fetchSavedTrack(trackId);
    if (!track) {
      return;
    }
    await saveCareerTrack({
      variables: {
        organizationId,
        careerTrackId: track.id,
        title: track.title,
        teamId: track.team?.id ?? null,
        deleteRoleIds: [roleId],
      },
    });
  };

  // Who the ladder hasn't reached yet. Refetched with the table, since
  // assigning someone from a role changes it, and on every visit, since
  // its link leads to the People table where roles get assigned too.
  const { data: unassignedData, refetch: refetchUnassigned } = useQuery<
    GetUnassignedMemberCountQuery,
    GetUnassignedMemberCountQueryVariables
  >(getUnassignedMemberCountQuery, {
    variables: { organizationId },
    fetchPolicy: "cache-and-network",
  });
  const unassignedCount = unassignedData?.unassignedMemberCount ?? 0;

  const tracks = useMemo(
    () =>
      tracksData
        ? uniqBy(assertEdgesNonNull(tracksData.careerTracks), "id")
        : [],
    [tracksData],
  );

  const competencies = useMemo(
    () =>
      competenciesData ? assertEdgesNonNull(competenciesData.competencies) : [],
    [competenciesData],
  );

  // Company-wide the way the track table means it: every role, or every
  // role of one type (all ICs, or all managers).
  const allRolesCompetencies = useMemo(
    () =>
      competencies.filter(
        (competency) => getCompetencyScopeSection(competency) === "company",
      ),
    [competencies],
  );
  const allRolesCountByLevel = useMemo(
    () => criteriaCountByLevel(allRolesCompetencies),
    [allRolesCompetencies],
  );

  // Takes a level out of the whole ladder, the same act as on All Roles: every
  // track's role on it and its shared criteria go, and levels above move down.
  // Unlike the rows' "Clear level", which only clears one row's criteria.
  const confirmLevelDeleteEverywhere = async (level: number) => {
    const { title, deleted } = summarizeLevelDeletion({
      level,
      competencies,
      careerTracks: tracks,
      label,
    });
    setDeleteLevelEverywhereTitle(title);
    setDeleteLevelEverywhereMessage(
      <LevelDeletionMessage level={level} deleted={deleted} />,
    );
    if (!(await askDeleteLevelEverywhere())) {
      return;
    }
    await deleteCareerLevel({ variables: { organizationId, level } });
    // A level only shown, never written on, goes with the column.
    setMinLevelCount((count) => Math.max(0, count - 1));
    await Promise.all([
      refetchTracks(),
      refetchCompetencies(),
      refetchUnassigned(),
    ]);
    successNotificationVar({ title: `Level ${level} deleted` });
  };

  // One entry per department, in title order, each with the tracks that sit
  // in it. Departments with no tracks keep their header: the way to put the
  // first one there is the header's own "+".
  const sections = useMemo(() => {
    const departments = teamsData?.organization
      ? sortBy(assertEdgesNonNull(teamsData.organization.teams), (team) =>
          team.title.toLowerCase(),
        )
      : [];
    const tracksByTeamId = new Map<number | string, typeof tracks>();
    sortBy(tracks, (track) => track.title.toLowerCase()).forEach((track) => {
      const key = track.team?.id ?? NO_DEPARTMENT;
      tracksByTeamId.set(key, [...(tracksByTeamId.get(key) ?? []), track]);
    });

    const departmentSections = departments.map((department) => ({
      key: `team-${department.id}`,
      team: department,
      title: department.title,
      tracks: tracksByTeamId.get(department.id) ?? [],
      competencies: competencies.filter((competency) =>
        isDepartmentCompetency(competency, department.id),
      ),
    }));

    const orphanTracks = tracksByTeamId.get(NO_DEPARTMENT) ?? [];
    // Only worth a section when something is in it: nothing is deliberately
    // filed under "no department".
    return orphanTracks.length > 0
      ? [
          ...departmentSections,
          {
            key: NO_DEPARTMENT,
            team: null,
            title: `No ${label("team", { capitalize: true })}`,
            tracks: orphanTracks,
            competencies: [],
          },
        ]
      : departmentSections;
  }, [competencies, label, teamsData, tracks]);

  // The ladder is as deep as its deepest role, or as the highest level
  // anything has criteria on.
  const levelCount = useMemo(() => {
    const levels = [
      ...tracks.flatMap((track) =>
        assertEdgesNonNull(track.roles).map((role) => role.level),
      ),
      ...Object.keys(allRolesCountByLevel).map(Number),
      // A department's own criteria can sit above its deepest role.
      ...sections.flatMap((section) =>
        section.team
          ? Object.keys(criteriaCountByLevel(section.competencies)).map(Number)
          : [],
      ),
    ];
    return Math.max(1, minLevelCount, ...levels);
  }, [allRolesCountByLevel, minLevelCount, sections, tracks]);

  // One button opens every department with tracks in it, or folds them all
  // once they're all open. Departments start open, so it starts as Collapse.
  const expandableSectionKeys = sections
    .filter((section) => section.tracks.length > 0)
    .map((section) => section.key);
  const isAllExpanded =
    expandableSectionKeys.length > 0 &&
    expandableSectionKeys.every(isSectionExpanded);

  // Everything the overview can make, in one menu, with the icons the
  // career track's Actions menu gives the same items.
  const createMenuOptions: DropdownOptionType[] = [
    {
      label: "Create Track",
      icon: <PlusCircleIcon className="h-4 w-4" />,
      onClick: () => setTrackDetails({ team: null }),
    },
    {
      label: `Create ${label("team", { capitalize: true })}`,
      icon: <OfficeBuildingIcon className="h-4 w-4" />,
      onClick: () => setIsCreatingDepartment(true),
    },
    { type: "divider" },
    {
      label: "Add Level",
      icon: <PlusIcon className="h-4 w-4" />,
      onClick: () => setMinLevelCount(levelCount + 1),
    },
  ];

  const countLabel = (count: number) =>
    `${count} ${label("competency", { pluralize: count !== 1 })}`;
  const emptyLabel = `No ${label("competency", { pluralize: true })}`;

  const table = (
    <div className="w-full rounded-md border overflow-hidden bg-white">
      <div className="relative overflow-x-auto">
        <table className="w-full text-sm table-fixed">
          {/* The header reads like the People table's: small gray labels on
              a light gray band. */}
          <thead className="text-xs font-medium text-gray-500">
            <tr>
              <th
                className={classNames(
                  nameColWidth,
                  "px-4 py-3 border border-l-0 border-t-0 sticky left-0 bg-gray-100 text-left font-medium",
                )}
              >
                Track
                <StickyColumnShadow />
              </th>
              {roleLevelIterator(levelCount).map((level) => (
                <th
                  key={level}
                  className={classNames(
                    levelColWidth,
                    "group/level px-4 py-3 border border-t-0 bg-gray-100 text-left font-medium",
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    Level {level}
                    <Button
                      theme={buttonTheme.iconGray}
                      icon={TrashIcon}
                      size={ButtonSize.medium}
                      className="-my-1 shrink-0 opacity-0 transition-opacity group-hover/level:opacity-100 focus:opacity-100"
                      tooltip="Delete level everywhere"
                      aria-label={`Overview: Delete level ${level}`}
                      onClick={() => confirmLevelDeleteEverywhere(level)}
                    />
                  </div>
                </th>
              ))}
              {/* The next level's column, offered where it would go. Nothing
                  is stored for a level alone, so this widens the table. */}
              <th className="min-w-16 px-4 py-2 border border-r-0 border-t-0 bg-gray-100 text-left font-normal">
                <Button
                  theme={buttonTheme.iconGray}
                  icon={TopicflowIconMap.plus}
                  size={ButtonSize.medium}
                  tooltip="Add level"
                  aria-label="Overview: Add level"
                  onClick={() => setMinLevelCount(levelCount + 1)}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {sections.map((section) => (
              <Fragment key={section.key}>
                {/* A department's row counts the competencies its tracks all
                    inherit, and opens their table. "No Department" only groups
                    what's left over. */}
                <SectionHeaderRow
                  title={section.title}
                  indentClassName="pl-4"
                  before={
                    <button
                      type="button"
                      // Pulled left by its padding, so the chevron lines up
                      // with the header's "Track" text above.
                      className="-ml-0.5 shrink-0 rounded-sm p-0.5 text-gray-500 hover:bg-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-default"
                      // Nothing to show under a department with no tracks.
                      disabled={section.tracks.length === 0}
                      aria-label={`${
                        isSectionExpanded(section.key) ? "Collapse" : "Expand"
                      } ${section.title}`}
                      aria-expanded={isSectionExpanded(section.key)}
                      onClick={(evt) => {
                        evt.stopPropagation();
                        setCollapsedSections((prev) =>
                          prev.includes(section.key)
                            ? prev.filter((key) => key !== section.key)
                            : [...prev, section.key],
                        );
                      }}
                    >
                      <TopicflowIcon
                        type={
                          isSectionExpanded(section.key)
                            ? "chevronDown"
                            : "chevronRight"
                        }
                        className="h-4 w-4"
                      />
                    </button>
                  }
                  after={
                    !isSectionExpanded(section.key) && (
                      <span className="shrink-0 text-xs font-normal text-gray-500">
                        {section.tracks.length}{" "}
                        {section.tracks.length === 1 ? "track" : "tracks"}
                      </span>
                    )
                  }
                  toneClassName={sectionHeaderToneClassName}
                  hoverClassName={sectionHeaderHoverToneClassName}
                  levelCount={levelCount}
                  countByLevel={
                    section.team
                      ? criteriaCountByLevel(section.competencies)
                      : {}
                  }
                  countLabel={countLabel}
                  emptyLabel={section.team ? emptyLabel : undefined}
                  onOpen={
                    section.team
                      ? () =>
                          link.redirect(
                            departmentCompetenciesPath(
                              organizationId,
                              assertNonNull(section.team).id,
                            ),
                          )
                      : undefined
                  }
                  onDeleteLevel={
                    section.team
                      ? (level) =>
                          confirmLevelDelete(
                            level,
                            assertNonNull(section.team).id,
                            section.title,
                            section.competencies,
                          )
                      : undefined
                  }
                  action={
                    <div
                      className={classNames(
                        rowActionClassName,
                        "flex items-center gap-1",
                      )}
                    >
                      {/* "No Department" is a grouping, not a department:
                          there's nothing to edit. */}
                      {section.team && (
                        <Button
                          theme={buttonTheme.iconGray}
                          icon={PencilIcon}
                          size={ButtonSize.medium}
                          tooltip={`Edit ${label("team")}`}
                          aria-label={`Overview: Edit ${label("team")} ${section.title}`}
                          onClick={(evt) => {
                            evt.stopPropagation();
                            handleEditDepartment(
                              assertNonNull(section.team).id,
                            );
                          }}
                        />
                      )}
                      <RowAddMenu
                        ariaLabel={`Overview: Add to ${section.title}`}
                        options={[
                          // "No Department" has nothing to scope one to.
                          ...(section.team
                            ? [
                                {
                                  label: `Create ${label("competency", {
                                    capitalize: true,
                                  })}`,
                                  onClick: () =>
                                    setCreatingCompetency({
                                      startingTeams: [
                                        assertNonNull(section.team),
                                      ],
                                    }),
                                },
                              ]
                            : []),
                          {
                            label: "Create Track",
                            onClick: () =>
                              setTrackDetails({
                                team: section.team
                                  ? {
                                      id: section.team.id,
                                      title: section.team.title,
                                      type: UserComboboxOptionType.TEAM,
                                    }
                                  : null,
                              }),
                          },
                        ]}
                      />
                    </div>
                  }
                />
                {isSectionExpanded(section.key) &&
                  section.tracks.map((track) => (
                    <tr
                      key={track.id}
                      className="group/row cursor-pointer hover:bg-gray-50"
                      role="link"
                      tabIndex={0}
                      aria-label={`Overview: Open ${track.title}`}
                      onClick={() =>
                        link.redirect(careerTrackPath(organizationId, track.id))
                      }
                      onKeyDown={(evt) => {
                        if (evt.key === "Enter") {
                          link.redirect(
                            careerTrackPath(organizationId, track.id),
                          );
                        }
                      }}
                    >
                      <td
                        className={classNames(
                          nameColWidth,
                          "pl-10 pr-4 py-2 border border-l-0 align-top font-medium sticky left-0 bg-white group-hover/row:bg-gray-50",
                        )}
                      >
                        <div className="flex items-center justify-between gap-2 min-w-0">
                          <div className="truncate" title={track.title}>
                            {track.title}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              theme={buttonTheme.iconGray}
                              icon={PencilIcon}
                              size={ButtonSize.medium}
                              className={rowActionClassName}
                              tooltip="Edit track details"
                              aria-label={`Overview: Edit track ${track.title}`}
                              onClick={(evt) => {
                                evt.stopPropagation();
                                setTrackDetails({
                                  track,
                                  team: track.team
                                    ? {
                                        id: track.team.id,
                                        title: track.team.title,
                                        type: UserComboboxOptionType.TEAM,
                                      }
                                    : null,
                                });
                              }}
                            />
                            <div className={rowActionClassName}>
                              <RowAddMenu
                                ariaLabel={`Overview: Add to ${track.title}`}
                                options={[
                                  {
                                    label: `Create ${label("competency", {
                                      capitalize: true,
                                    })}`,
                                    onClick: () =>
                                      setCreatingCompetency({
                                        startingCareerTracks: [
                                          { id: track.id, title: track.title },
                                        ],
                                      }),
                                  },
                                  {
                                    label: "Add Level",
                                    onClick: () => startAddingLevel(track),
                                  },
                                ]}
                              />
                            </div>
                          </div>
                        </div>
                        <StickyColumnShadow />
                      </td>
                      {roleLevelIterator(levelCount).map((level) => {
                        const role = assertEdgesNonNull(track.roles).find(
                          (role) => role.level === level,
                        );
                        const members = role
                          ? assertEdgesNonNull(role.assignedUsers)
                          : [];
                        return (
                          <td
                            key={level}
                            className={classNames(
                              levelColWidth,
                              "group/cell px-4 py-2 border",
                              "align-top",
                              !role && "text-center",
                            )}
                          >
                            {role ? (
                              <div className="flex items-center justify-between gap-2 min-w-0">
                                <div className="truncate" title={role.title}>
                                  {role.title}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  {/* A count rather than faces: at a glance the
                                    question is how many sit here, not who. */}
                                  {members.length > 0 && (
                                    <span className="flex items-center gap-1 text-xs text-gray-500">
                                      <TopicflowIcon
                                        type="user"
                                        className="h-3.5 w-3.5 shrink-0"
                                      />
                                      {members.length}
                                    </span>
                                  )}
                                  <Button
                                    theme={buttonTheme.iconGray}
                                    icon={PencilIcon}
                                    size={ButtonSize.medium}
                                    className="shrink-0 opacity-0 transition-opacity group-hover/cell:opacity-100 focus:opacity-100"
                                    tooltip="Edit role"
                                    aria-label={`Overview: Edit ${role.title}`}
                                    onClick={(evt) => {
                                      evt.stopPropagation();
                                      setEditingRole({
                                        track,
                                        role: {
                                          id: role.id,
                                          uuid: String(role.id),
                                          level: role.level,
                                          title: role.title,
                                          roleType: role.roleType,
                                          assignToUsers: [...members],
                                          criteriaByCompetencyId: {},
                                          responsibilities: [],
                                        },
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                            ) : (
                              // Nothing here yet: the cell offers the role
                              // rather than marking its absence. Laid out like
                              // the filled cells, so every button in the row
                              // sits on the same line.
                              <div className="flex items-center justify-center">
                                <AddButton
                                  icon="userPlus01"
                                  size={ButtonSize.medium}
                                  className={rowActionClassName}
                                  text="Add role for this track"
                                  aria-label={`Overview: Add role to ${track.title} level ${level}`}
                                  onClick={(evt) => {
                                    evt.stopPropagation();
                                    setEditingRole({
                                      track,
                                      role: {
                                        uuid: `new-${track.id}-${level}`,
                                        level,
                                        title: "",
                                        roleType: CareerTrackRoleType.Ic,
                                        assignToUsers: [],
                                        criteriaByCompetencyId: {},
                                        responsibilities: [],
                                      },
                                    });
                                  }}
                                />
                              </div>
                            )}
                          </td>
                        );
                      })}
                      <td className="min-w-16 px-4 py-2 border border-r-0 align-top bg-white group-hover/row:bg-gray-50">
                        <Button
                          theme={buttonTheme.iconGray}
                          icon={TopicflowIconMap.plus}
                          size={ButtonSize.medium}
                          className={rowActionClassName}
                          tooltip="Add level"
                          aria-label={`Overview: Add level to ${track.title}`}
                          onClick={(evt) => {
                            evt.stopPropagation();
                            startAddingLevel(track);
                          }}
                        />
                      </td>
                    </tr>
                  ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <>
      <ConfirmDeleteTrackDialog />
      <ConfirmDeleteLevelDialog />
      <ConfirmDeleteLevelEverywhereDialog />
      {creatingCompetency && (
        <CompetencyFormModal
          organizationId={organizationId}
          startingTeams={creatingCompetency.startingTeams}
          startingCareerTracks={creatingCompetency.startingCareerTracks}
          onClose={() => setCreatingCompetency(null)}
          onCompetencySaved={() => {
            setCreatingCompetency(null);
            // The competencies table below keeps its own copy: both refresh.
            client.refetchQueries({
              include: [getOrganizationCompetenciesQuery],
            });
          }}
        />
      )}
      {editingDepartment && (
        <TeamEditDialog
          organization={{ id: organizationId }}
          team={editingDepartment}
          onClose={() => {
            setEditingDepartment(null);
            // Renamed or deleted: tracks show their department's name, and
            // deleting a department deletes its tracks too, unassigning
            // their people and unscoping its competencies.
            refetchTeams();
            refetchTracks();
            refetchUnassigned();
            client.refetchQueries({
              include: [getOrganizationCompetenciesQuery],
            });
          }}
        />
      )}
      {editingRole && (
        <RoleDetailModal
          organizationId={organizationId}
          level={editingRole.role.level ?? 1}
          role={editingRole.role}
          careerTrackData={{
            organizationId,
            careerTrackId: editingRole.track.id,
            title: editingRole.track.title,
          }}
          associatedTeam={
            editingRole.track.team
              ? {
                  id: editingRole.track.team.id,
                  title: editingRole.track.team.title,
                  type: UserComboboxOptionType.TEAM,
                }
              : null
          }
          onRoleChange={(role) =>
            handleRoleChange(editingRole.track, editingRole.role, role)
          }
          onDelete={
            editingRole.role.id
              ? () =>
                  handleRoleDelete(
                    editingRole.track,
                    assertNonNull(editingRole.role.id),
                  )
              : undefined
          }
          deleteConfirmation={{
            title: "Delete role?",
            message: `Delete "${editingRole.role.title}", with its role-specific criteria and responsibilities? ${describeRoleHolders(
              editingRole.role.assignToUsers,
            )} ${label("review", {
              capitalize: true,
            })} questions and answers about this role are deleted too.`,
          }}
          onClose={() => setEditingRole(null)}
        />
      )}
      {isCreatingDepartment && (
        <TeamEditDialog
          organization={{ id: organizationId }}
          onClose={() => setIsCreatingDepartment(false)}
          onTeamSaved={() => {
            setIsCreatingDepartment(false);
            refetchTeams();
          }}
        />
      )}
      {trackDetails && (
        <CareerTrackDetailsModal
          organizationId={organizationId}
          title={trackDetails.track?.title ?? ""}
          team={trackDetails.team}
          onClose={() => setTrackDetails(null)}
          onDelete={
            trackDetails.track
              ? () => confirmTrackDelete(assertNonNull(trackDetails.track))
              : undefined
          }
          onChange={({ title, team }) => {
            const { track } = trackDetails;
            return track
              ? handleTrackDetailsChange(track, title, team)
              : handleCreateTrack(title, team);
          }}
        />
      )}
      <SettingsPageHeader
        breadcrumbs={getRolesAndCompetenciesBreadcrumbs(organizationId, label)}
      />

      <div>
        {/* Named on the left with what it can make on the right, the way the
            competencies list is laid out. */}
        <div className="flex items-center justify-between gap-2 mb-3 shrink-0">
          <div className="text-lg font-bold">Career Track Overview</div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Where to open one department, track or person from here,
                the same picker those pages have. */}
            <CareerTrackViewSwitcher
              organizationId={organizationId}
              isOverview
            />
            <Button
              size={ButtonSize.medium}
              aria-label={
                isAllExpanded
                  ? "Overview: Collapse all departments"
                  : "Overview: Expand all departments"
              }
              disabled={expandableSectionKeys.length === 0}
              onClick={() =>
                setCollapsedSections(isAllExpanded ? expandableSectionKeys : [])
              }
            >
              <TopicflowIcon
                type={
                  isAllExpanded ? "alignVerticalCenter01" : "spacingHeight01"
                }
                className="h-4 w-4"
              />
              {isAllExpanded ? "Collapse All" : "Expand All"}
            </Button>
            <Dropdown align="end" options={createMenuOptions}>
              <Button size={ButtonSize.medium} aria-label="Overview: Create">
                <TopicflowIcon type="plus" className="h-4 w-4" />
                Create/Add
              </Button>
            </Dropdown>
          </div>
        </div>
        {tracksData && tracks.length === 0 && (
          <NoTracksNotice organizationId={organizationId} />
        )}
        {tracksLoading && teamsLoading && tracks.length === 0 ? (
          <div className="flex justify-center mt-8">
            <Loading />
          </div>
        ) : (
          <>
            {/* With no departments and no tracks there's nothing to lay out
                by level; the notice above says what to do first. */}
            {sections.length > 0 && table}
            {/* With no tracks there are no roles to be assigned to, and the
                notice above already says so. */}
            {unassignedCount > 0 && tracks.length > 0 && (
              <AppLink
                to={membersWithoutRolePath(organizationId)}
                className="mt-2 inline-block text-xs text-gray-500 hover:underline"
              >
                {`${unassignedCount} ${
                  unassignedCount === 1 ? "person" : "people"
                } in your ${label("organization")} ${
                  unassignedCount === 1 ? "isn't" : "aren't"
                } assigned to a role.`}
              </AppLink>
            )}
          </>
        )}
      </div>

      {/* Every competency, under the tracks that use them: what used to be
          the Competencies tab. */}
      <div className="mt-10">
        <CompetenciesList organizationId={organizationId} />
      </div>
    </>
  );
};

export default CareerTrackOverview;
